import { Link } from "react-router-dom";
import { ArrowLeft, Shield } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto px-5 py-10 lg:py-16">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 no-underline mb-6" data-testid="back-link">
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-xl bg-[#FF6B00] text-white flex items-center justify-center"><Shield className="h-5 w-5" /></div>
          <div>
            <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-extrabold text-slate-900">Privacy Policy</h1>
            <p className="text-xs text-slate-500">Last updated: 25 April 2026</p>
          </div>
        </div>

        <div className="prose prose-sm max-w-none mt-8 text-slate-700 leading-relaxed space-y-5 text-[14.5px]">
          <p>
            PickNGo ("we", "us", "our") operates the website <span className="font-mono">pickngo.co.in</span> and the
            associated logistics services across India. This Privacy Policy explains how we collect, use, disclose, and
            safeguard your personal information when you use our platform.
          </p>

          <h2 className="font-['Outfit'] text-xl font-bold text-slate-900">1. Information We Collect</h2>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><b>Account data:</b> name, email, phone, address, role (customer / agent / B2B).</li>
            <li><b>KYC data (agents):</b> ID proof, vehicle type, photo, bank account for payouts.</li>
            <li><b>Order data:</b> pickup &amp; drop addresses, package details, weight, dimensions.</li>
            <li><b>Payment data:</b> processed via Razorpay; we never store full card numbers.</li>
            <li><b>Location data:</b> agent live GPS during active deliveries (consented at app login).</li>
            <li><b>Device &amp; usage:</b> IP, browser, device, pages visited, click events.</li>
          </ul>

          <h2 className="font-['Outfit'] text-xl font-bold text-slate-900">2. How We Use Your Information</h2>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>To create your account and authenticate you securely.</li>
            <li>To match orders with available agents and route shipments via our hubs.</li>
            <li>To process payments, refunds, and damage claims.</li>
            <li>To send order updates, OTPs, support emails, and (with consent) marketing.</li>
            <li>To prevent fraud, abuse, and to enforce our Terms of Service.</li>
            <li>To comply with Indian GST, IT Act 2000 and other applicable laws.</li>
          </ul>

          <h2 className="font-['Outfit'] text-xl font-bold text-slate-900">3. Sharing &amp; Third Parties</h2>
          <p>
            We share data only as needed to deliver our service: (a) gig agents — limited to pickup/drop info for the
            assigned order; (b) Razorpay for payments; (c) MongoDB Atlas for hosted database storage; (d) Emergent for
            cloud hosting; (e) law-enforcement when legally compelled. We never sell your data.
          </p>

          <h2 className="font-['Outfit'] text-xl font-bold text-slate-900">4. Data Retention</h2>
          <p>
            Order, invoice, and payment records: 7 years (tax compliance). KYC/agent records: 3 years post-deactivation.
            Marketing preferences: until you unsubscribe. Account deletion requests are honoured within 30 days subject
            to the above legal retention windows.
          </p>

          <h2 className="font-['Outfit'] text-xl font-bold text-slate-900">5. Security</h2>
          <p>
            Passwords are hashed with bcrypt. Auth tokens are HttpOnly + Secure. Communications are TLS 1.2+. Data is
            encrypted at rest on MongoDB Atlas. Despite best practices, no internet system is 100% secure — please use a
            strong, unique password.
          </p>

          <h2 className="font-['Outfit'] text-xl font-bold text-slate-900">6. Your Rights</h2>
          <p>
            You may access, correct, export, or delete your personal data at any time by emailing
            <a href="mailto:support@pickngo.co.in" className="text-[#FF6B00] no-underline"> support@pickngo.co.in</a>.
            For grievance redressal under India's IT Rules 2021 contact our Grievance Officer at the same email within
            15 working days.
          </p>

          <h2 className="font-['Outfit'] text-xl font-bold text-slate-900">7. Children</h2>
          <p>The platform is not intended for users under 18.</p>

          <h2 className="font-['Outfit'] text-xl font-bold text-slate-900">8. Updates</h2>
          <p>
            We may update this Policy. The "Last updated" date at the top reflects the latest version. Continued use of
            the platform after an update means you accept the changes.
          </p>

          <h2 className="font-['Outfit'] text-xl font-bold text-slate-900">9. Contact</h2>
          <p>
            <b>PickNGo Logistics</b><br />
            Email: <a href="mailto:support@pickngo.co.in" className="text-[#FF6B00] no-underline">support@pickngo.co.in</a><br />
            Address: Khammam, Telangana, India
          </p>
        </div>
      </div>
    </div>
  );
}
